import { Link } from "react-router-dom";
import Header from "@/pages/home/components/Header";
import Footer from "@/pages/home/components/Footer";

const sportsCategories = [
  {
    icon: "ri-football-line",
    title: "해외축구 및 국내축구",
    desc: "프리미어리그, 라리가, 분데스리가, K리그 등 주요 축구 경기",
    color: "bg-primary-500",
  },
  {
    icon: "ri-basketball-line",
    title: "KBL, NBA 농구",
    desc: "국내 한국프로농구와 미국 NBA 경기",
    color: "bg-accent-500",
  },
  {
    icon: "ri-focus-line",
    title: "MLB, KBO 야구",
    desc: "메이저리그와 한국프로야구 경기",
    color: "bg-secondary-500",
  },
  {
    icon: "ri-earth-line",
    title: "주요 국제대회 및 빅매치 경기",
    desc: "월드컵, 올림픽, 챔피언스리그 등 주요 국제 경기",
    color: "bg-primary-400",
  },
];

const watchSteps = [
  "월드컵티비 메인 페이지에 접속합니다.",
  "상단 또는 게시판에서 원하는 경기를 선택합니다.",
  "경기 시작 시간에 맞춰 중계 페이지에 접속합니다.",
  "안내된 방식에 따라 스포츠중계를 시청하시면 됩니다.",
];

const scheduleTips = [
  "인기 경기는 공지사항 또는 상단 영역을 통해 우선 안내됩니다.",
  "정확한 경기 일정은 공지사항 게시판을 함께 확인해 주세요.",
];

const cautions = [
  "네트워크 환경에 따라 시청 품질에 차이가 있을 수 있습니다.",
  "원활한 이용을 위해 최신 브라우저 사용을 권장합니다.",
  "의심스러운 외부 링크나 광고는 주의해 주세요.",
];

const relatedGuides = [
  { label: "스포츠중계 이용가이드", href: "/guide" },
  { label: "월드컵티비 사이트 이용방법 안내", href: "/guide" },
  { label: "자주 묻는 질문(FAQ)", href: "/faq" },
];

export default function GuidePage() {
  return (
    <div className="min-h-screen bg-background-50">
      <Header />

      {/* Page Header */}
      <section className="bg-background-100 border-b border-background-300">
        <div className="max-w-[1000px] mx-auto px-3 md:px-4 py-6 md:py-8">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm text-foreground-600 hover:text-primary-400 transition-colors mb-3"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-left-line" />
            </span>
            홈으로
          </Link>
          <div className="flex items-center gap-3">
            <span className="w-9 h-9 flex items-center justify-center text-primary-500">
              <i className="ri-book-open-line text-2xl" />
            </span>
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-foreground-700">이용가이드</h1>
              <p className="text-sm text-foreground-600 mt-0.5">
                월드컵티비 시즌2를 안전하고 효과적으로 이용하는 방법
              </p>
            </div>
          </div>
        </div>
      </section>

      <main className="max-w-[1000px] mx-auto px-3 md:px-4 py-6 md:py-8">
        {/* Title */}
        <div className="text-center mb-10">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground-800 mb-4">
            스포츠중계 이용가이드
          </h2>
          <p className="text-sm text-foreground-600 leading-relaxed max-w-2xl mx-auto">
            월드컵티비 실시간 스포츠중계 보는 방법 총정리
            <br />
            이 글은 월드컵티비를 처음 이용하시는 분들을 위한 스포츠중계 이용가이드입니다.
            <br />
            처음 접속하셨을 때 어떻게 이용해야 하는지, 어떤 스포츠중계가 제공되는지 한 번에 이해하실 수 있도록 정리했습니다.
          </p>
        </div>

        {/* Section 1: 제공하는 스포츠중계 */}
        <div className="mb-10">
          <h3 className="text-lg md:text-xl font-bold text-foreground-800 mb-5 pb-3 border-b-2 border-primary-500">
            월드컵티비에서 제공하는 스포츠중계
          </h3>
          <p className="text-sm text-foreground-600 mb-5">
            월드컵티비에서는 다양한 종목의 스포츠중계를 제공합니다.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {sportsCategories.map((cat) => (
              <div
                key={cat.title}
                className="flex items-start gap-3 p-4 rounded-lg bg-background-100 border border-background-300 hover:border-primary-500/30 transition-colors"
              >
                <div className={`w-10 h-10 rounded-lg ${cat.color} flex items-center justify-center flex-shrink-0`}>
                  <span className="w-5 h-5 flex items-center justify-center text-foreground-50 text-lg">
                    <i className={cat.icon} />
                  </span>
                </div>
                <div>
                  <h4 className="text-sm font-bold text-foreground-700">{cat.title}</h4>
                  <p className="text-xs text-foreground-600 mt-0.5">{cat.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 p-3 rounded-lg bg-accent-100/60 border border-accent-300/40">
            <p className="text-sm text-accent-700">
              <i className="ri-lightbulb-line mr-1" />
              <strong>팁:</strong> 경기 일정에 따라 중계 정보는 순차적으로 업데이트됩니다.
            </p>
          </div>
        </div>

        {/* Section 2: 시청 방법 */}
        <div className="mb-10">
          <h3 className="text-lg md:text-xl font-bold text-foreground-800 mb-5 pb-3 border-b-2 border-primary-500">
            스포츠중계 시청 방법 안내
          </h3>
          <ol className="space-y-3 mb-5">
            {watchSteps.map((step, idx) => (
              <li key={idx} className="flex items-start gap-3">
                <div className="w-7 h-7 rounded-full bg-primary-500 flex items-center justify-center flex-shrink-0">
                  <span className="text-xs font-black text-foreground-50">{idx + 1}</span>
                </div>
                <p className="text-sm text-foreground-700 pt-0.5">{step}</p>
              </li>
            ))}
          </ol>
          <div className="p-4 rounded-lg bg-secondary-100/60 border border-secondary-300/40">
            <p className="text-sm text-secondary-700">
              <i className="ri-information-line mr-1" />
              <strong>별도의 복잡한 설치 없이 PC와 모바일 모두 이용 가능합니다.</strong>
            </p>
          </div>
        </div>

        {/* Section 3: 중계 일정 */}
        <div className="mb-10">
          <h3 className="text-lg md:text-xl font-bold text-foreground-800 mb-5 pb-3 border-b-2 border-primary-500">
            중계 일정 확인 방법
          </h3>
          <p className="text-sm text-foreground-600 mb-4">
            스포츠중계 일정은 보통 경기 당일 기준으로 업데이트됩니다.
          </p>
          <ul className="space-y-2">
            {scheduleTips.map((tip, idx) => (
              <li key={idx} className="flex items-start gap-2 text-sm text-foreground-600">
                <span className="w-1.5 h-1.5 rounded-full bg-primary-500 mt-2 flex-shrink-0" />
                {tip}
              </li>
            ))}
          </ul>
        </div>

        {/* Section 4: 유의사항 */}
        <div className="mb-10">
          <h3 className="text-lg md:text-xl font-bold text-foreground-800 mb-5 pb-3 border-b-2 border-primary-500">
            스포츠중계 이용 시 유의사항
          </h3>
          <div className="p-4 rounded-lg bg-red-500/5 border border-red-500/15">
            <ul className="space-y-2">
              {cautions.map((caution, idx) => (
                <li key={idx} className="flex items-start gap-2 text-sm text-foreground-600">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-400 mt-2 flex-shrink-0" />
                  {caution}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Related Guides */}
        <div className="mb-8">
          <h3 className="text-lg md:text-xl font-bold text-foreground-800 mb-4 pb-3 border-b-2 border-primary-500">
            함께 보면 좋은 이용가이드
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {relatedGuides.map((guide) => (
              <Link
                key={guide.label}
                to={guide.href}
                className="flex items-center gap-2 p-4 rounded-lg bg-background-100 border border-background-300 hover:border-primary-500/30 hover:bg-background-200/50 transition-colors"
              >
                <span className="w-5 h-5 flex items-center justify-center text-primary-400">
                  <i className="ri-arrow-right-s-fill" />
                </span>
                <span className="text-sm font-medium text-primary-500">{guide.label}</span>
              </Link>
            ))}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}